import { useCallback, useEffect, useState } from 'react';

import { TabData } from '@/__core/types/TabData';

interface UseTabStorageProps {
  storageKey: string;
  defaultTabs: TabData[];
  maxTabs?: number;
}

export const useTabStorage = ({ storageKey, defaultTabs, maxTabs = 20 }: UseTabStorageProps) => {
  const [tabs, setTabs] = useState<TabData[]>(() => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return defaultTabs;
    }

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : defaultTabs;
    } catch {
      return defaultTabs;
    }
  });

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(tabs));
  }, [storageKey, tabs]);

  useEffect(() => {
    localStorage.setItem(`${storageKey}_active`, activeTab.toString());
  }, [storageKey, activeTab]);

  useEffect(() => {
    const stored = localStorage.getItem(`${storageKey}_active`);
    if (!stored) {
      return;
    }

    const parsed = Number.parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed < tabs.length) {
      setActiveTab(parsed);
    }
  }, [storageKey, tabs.length]);

  const addTab = useCallback(
    (newTab: TabData) => {
      setTabs((prev) => {
        const isNewEntityTab = typeof newTab.type === 'string' && newTab.type.startsWith('new-');
        const isUpdateEntityTab = typeof newTab.type === 'string' && newTab.type.startsWith('update-');

        const existingIndex = prev.findIndex((tab) => {
          if (tab.type !== newTab.type) {
            return false;
          }

          if (isNewEntityTab || newTab.type === 'new-inventory-record') {
            return false;
          }

          if ((isUpdateEntityTab || newTab.type === 'update-inventory-record') && newTab.entityId) {
            return tab.entityId === newTab.entityId;
          }

          return tab.type === newTab.type;
        });

        if (existingIndex !== -1) {
          setActiveTab(existingIndex);
          return prev;
        }

        const closableTabs = prev.filter((tab) => tab.closable !== false);
        if (closableTabs.length >= maxTabs) {
          const oldestTab = closableTabs
            .filter((tab) => tab.type !== 'list' && tab.type !== 'add')
            .sort((left, right) => (left.createdAt || 0) - (right.createdAt || 0))[0];

          if (oldestTab) {
            prev = prev.filter((tab) => tab.id !== oldestTab.id);
          }
        }

        const tabWithTimestamp: TabData = {
          ...newTab,
          id: newTab.id || Date.now(),
          createdAt: Date.now(),
        };

        const nextTabs = [...prev.slice(0, -1), tabWithTimestamp, prev[prev.length - 1]];
        setActiveTab(nextTabs.length - 2);
        return nextTabs;
      });
    },
    [maxTabs]
  );

  const closeTab = useCallback(
    (tabId: number) => {
      setTabs((prev) => {
        const removedIndex = prev.findIndex((tab) => tab.id === tabId);
        const nextTabs = prev.filter((tab) => tab.id !== tabId);

        if (removedIndex === activeTab && nextTabs.length > 0) {
          setActiveTab(Math.max(0, removedIndex - 1));
        } else if (removedIndex < activeTab) {
          setActiveTab((current) => Math.max(0, current - 1));
        }

        return nextTabs;
      });
    },
    [activeTab]
  );

  const updateTab = useCallback((tabId: number, updates: Partial<TabData>) => {
    setTabs((prev) => prev.map((tab) => (tab.id === tabId ? { ...tab, ...updates } : tab)));
  }, []);

  const resetTabs = useCallback(() => {
    setTabs(defaultTabs);
    setActiveTab(0);
  }, [defaultTabs]);

  return {
    tabs,
    activeTab,
    setActiveTab,
    addTab,
    closeTab,
    updateTab,
    resetTabs,
  };
};